import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Truck, Package, ExternalLink, Mail } from "lucide-react";

const CARRIER_TRACK_URL = {
  ups: (n) => `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(n)}`,
  usps: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  fedex: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  dhl: (n) => `https://www.dhl.com/global-en/home/tracking/tracking-parcel.html?submit=1&tracking-id=${encodeURIComponent(n)}`,
  other: () => null,
};

const CARRIER_LABELS = { ups: "UPS", usps: "USPS", fedex: "FedEx", dhl: "DHL", other: "Carrier" };

function StatusPill({ status }) {
  const isShipped = status === "shipped";
  const isRefunded = status === "refunded" || status === "partially_refunded";
  return (
    <span
      data-testid="track-status"
      className={`inline-block font-mono text-[10px] uppercase tracking-editorial border px-2 py-1 ${
        isShipped ? "border-[#7A2021] text-[#7A2021]" :
        isRefunded ? "border-[#1A1714]/40 text-[#1A1714]/60" :
        "border-[#1A1714] text-[#1A1714]"
      }`}
    >
      {status || "—"}
    </span>
  );
}

export default function TrackOrder() {
  const { orderId } = useParams();
  const [params, setParams] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [inputEmail, setInputEmail] = useState(initialEmail);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTracking = async (em) => {
    if (!em) return;
    setLoading(true);
    setError("");
    try {
      const { data: d } = await api.get(`/orders/track/${orderId}`, { params: { email: em } });
      setData(d);
    } catch (e) {
      const msg = e.response?.status === 404
        ? "We couldn't find an order that matches this number and email."
        : (e.response?.data?.detail || "Something went wrong. Please try again.");
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialEmail) fetchTracking(initialEmail);
    // eslint-disable-next-line
  }, [orderId]);

  const submit = (e) => {
    e.preventDefault();
    const em = inputEmail.trim();
    if (!em) {
      setError("Please enter the email you used at checkout.");
      return;
    }
    setEmail(em);
    setParams({ email: em });
    fetchTracking(em);
  };

  const carrierKey = data?.carrier || "other";
  const trackHref = data?.tracking_number ? (CARRIER_TRACK_URL[carrierKey]?.(data.tracking_number) || null) : null;

  return (
    <div className="pt-28 md:pt-40 max-w-3xl mx-auto px-6 md:px-10 pb-24" data-testid="track-order-page">
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Ch. 11 — In transit</p>
      <h1 className="font-serif text-2xl sm:text-3xl md:text-6xl tracking-tight leading-none mt-3 flex flex-wrap items-center gap-4">
        Track order <span className="text-[#7A2021] break-all">#{orderId}</span>
      </h1>

      {!email && (
        <form onSubmit={submit} className="mt-10 border border-[#E5E1D8] p-6 space-y-4" data-testid="track-email-gate">
          <p className="font-serif text-lg text-[#1A1714]/80">
            Enter the email you used at checkout to view live shipment status.
          </p>
          <div className="flex flex-col md:flex-row gap-3">
            <Input
              type="email"
              value={inputEmail}
              onChange={(e) => setInputEmail(e.target.value)}
              placeholder="you@example.com"
              className="rounded-none border-[#E5E1D8] h-11 flex-1"
              data-testid="track-email-input"
              required
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="track-email-submit"
              className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-11"
            >
              <Mail size={14} className="mr-2" /> View tracking
            </Button>
          </div>
          {error && <p className="font-mono text-[11px] text-[#7A2021]" data-testid="track-error">{error}</p>}
        </form>
      )}

      {email && loading && (
        <p className="mt-10 font-serif italic text-xl text-[#1A1714]/60" data-testid="track-loading">Looking up your shipment…</p>
      )}

      {email && !loading && error && (
        <div className="mt-10 border border-[#7A2021] p-6" data-testid="track-error-box">
          <p className="font-serif text-xl text-[#7A2021]">{error}</p>
          <button
            onClick={() => { setEmail(""); setInputEmail(""); }}
            className="mt-4 font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/70 underline"
            data-testid="track-try-again"
          >
            Try a different email
          </button>
        </div>
      )}

      {email && !loading && data && (
        <div className="mt-10 space-y-8" data-testid="track-order-content">
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={data.status} />
            {data.shipped_at && (
              <span className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60" data-testid="track-shipped-at">
                Shipped {new Date(data.shipped_at).toLocaleString()}
              </span>
            )}
          </div>

          {data.tracking_number ? (
            <div className="border border-[#E5E1D8] p-6 space-y-6" data-testid="track-details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Carrier</p>
                  <p className="font-serif text-3xl text-[#1A1714] mt-1" data-testid="track-carrier">
                    {CARRIER_LABELS[carrierKey] || "Carrier"}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Tracking number</p>
                  <p className="font-mono text-2xl text-[#7A2021] mt-1 break-all" data-testid="track-number">
                    {data.tracking_number}
                  </p>
                </div>
              </div>

              {trackHref ? (
                <a
                  href={trackHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="track-carrier-link"
                  className="inline-flex items-center gap-2 bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial px-6 py-3 transition-colors"
                >
                  <ExternalLink size={14} />
                  View live status on {CARRIER_LABELS[carrierKey]}
                </a>
              ) : (
                <p className="font-mono text-[11px] text-[#1A1714]/60">
                  Live carrier lookup not available for “{CARRIER_LABELS[carrierKey]}” — please contact the carrier directly with the tracking number above.
                </p>
              )}
            </div>
          ) : (
            <div className="border border-[#E5E1D8] p-6 flex items-center gap-4" data-testid="track-not-shipped">
              <Package className="text-[#1A1714]/40" size={28} />
              <div>
                <p className="font-serif text-2xl text-[#1A1714]">Your order is being prepared</p>
                <p className="font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/60 mt-1">
                  We'll email you a tracking number the moment it ships.
                </p>
              </div>
            </div>
          )}

          {data.ship_to && (
            <div className="border border-[#E5E1D8] p-6" data-testid="track-shipto">
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 flex items-center gap-2">
                <Truck size={12} /> Shipping to
              </p>
              <p className="font-serif text-xl text-[#1A1714] mt-2">
                {data.ship_to.full_name || "—"}
              </p>
              <p className="font-mono text-xs text-[#1A1714]/70 mt-1">
                {[data.ship_to.city, data.ship_to.state, data.ship_to.postal_code, data.ship_to.country].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/60">
                {data.items_count} {data.items_count === 1 ? "item" : "items"} in this shipment
              </p>
            </div>
          )}

          <Link
            to="/shop"
            data-testid="track-continue-shopping"
            className="inline-block font-mono text-[11px] uppercase tracking-editorial text-[#1A1714]/70 hover:text-[#7A2021] underline"
          >
            ← Continue browsing the shop
          </Link>
        </div>
      )}
    </div>
  );
}
